/**
 * ProposalSummaryCard Component
 * Displays AI-generated proposal summary with key points and capabilities
 */

import React from 'react';
import { Card } from '@tracekit/core';
import { useProposalSummary, ProposalSummaryResponse } from '../hooks/useProposalSummary';
import { useWorkspace } from '@tracekit/core';
import {
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@tracekit/core';

interface ProposalSummaryCardProps {
  opportunityId: string | null;
  className?: string;
}

export function ProposalSummaryCard({ opportunityId, className = '' }: ProposalSummaryCardProps) {
  const { projectId } = useWorkspace();
  
  // Use useMemo to stabilize the request object to prevent unnecessary re-fetches
  const request = React.useMemo(() => ({
    include_past_proposals: true,
    include_capabilities: true,
    include_sops: true,
    max_length: 500,
  }), []);
  
  const {
    data: summary,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useProposalSummary(opportunityId, projectId || undefined, request, !!opportunityId && !!projectId);

  if (!opportunityId) {
    return null;
  }

  // Convert markdown to HTML for proper formatting
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';
    
    let html = markdown;
    
    // Escape HTML first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    
    // Bold text (**text** or __text__)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
    
    // Italic text (*text* or _text_)
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    
    // Headers
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-gray-900 dark:text-theme-text mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-gray-900 dark:text-theme-text mt-5 mb-3">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-gray-900 dark:text-theme-text mt-6 mb-4">$1</h1>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p class="mb-3">');
    html = html.replace(/\n/g, '<br />');
    
    // Wrap in paragraph if not already wrapped
    if (!html.startsWith('<')) {
      html = `<p class="mb-3">${html}</p>`;
    }
    
    return html;
  };

  return (
    <Card className={`p-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 ${className}`}>
      {/* Ultra-Condensed Header */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1">
          <Sparkles className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-sm text-gray-900 dark:text-theme-text">
            Proposal AI Summary
          </h3>
        </div>
        {summary && (
          <span className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-theme-text-secondary">
            {summary.summary_source === 'rag_analysis' ? 'AI' : 'Basic'}
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
              Generating AI summary...
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="py-6">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                Failed to generate summary
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isRefetching}
                className="text-xs"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Content */}
      {summary && !isLoading && (
        <div className="space-y-3">
          {/* Main Summary with Markdown Rendering */}
          <div 
            className="text-sm text-gray-700 dark:text-theme-text-secondary leading-relaxed"
            dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(summary.summary) }}
          />

          {/* Key Points */}
          {summary.key_points && summary.key_points.length > 0 && (
            <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-theme-text mb-1.5 flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                Key Points
              </h4>
              <ul className="space-y-1">
                {summary.key_points.map((point, index) => (
                  <li key={index} className="text-xs text-gray-700 dark:text-theme-text-secondary flex items-start gap-1.5">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Relevant Capabilities */}
          {summary.relevant_capabilities && summary.relevant_capabilities.length > 0 && (
            <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-theme-text mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                Relevant Capabilities
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {summary.relevant_capabilities.map((capability, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

